import {
  closestCenter,
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { Loader2, Radio } from "lucide-react";
import React, { useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { actorFromAuth } from "src/lib/activityService";
import { canManageService, ServiceDomain } from "src/lib/roles";
import { selectUser, selectUserProfile } from "src/store/authSlice";
import { useHappeningNowParallaxStore } from "src/store/happeningNowParallaxStore";
import type { HappeningNowListItem } from "src/types/happeningNow";
import { useShallow } from "zustand/react/shallow";
import { Switch } from "../ui/Switch";
import { LiveCarouselCard } from "./LiveCarouselCard";

interface LiveCarouselSectionProps {
  items: HappeningNowListItem[];
  loading: boolean;
  busyId: string | null;
  /** Lookup of draft cards by id — used to show "draft edit pending" tag. */
  draftById: Map<string, HappeningNowListItem>;
  readOnly?: boolean;
  onEditInDraft: (item: HappeningNowListItem) => void;
  onToggleActive: (item: HappeningNowListItem) => void;
  onDragEnd: (event: DragEndEvent) => void;
}

function isDraftDifferentFromLive(
  draft: HappeningNowListItem,
  live: HappeningNowListItem,
): boolean {
  return (
    draft.title !== live.title ||
    (draft.description ?? "") !== (live.description ?? "") ||
    draft.imageUrl !== live.imageUrl ||
    draft.targetLink !== live.targetLink ||
    draft.expiresAt.toMillis() !== live.expiresAt.toMillis() ||
    draft.active !== live.active
  );
}

/**
 * "Live carousel" section — always rendered above the draft so the operator
 * can see at a glance what end users are seeing right now. Reorder + pause
 * apply to live directly; everything else routes through draft.
 */
export const LiveCarouselSection: React.FC<LiveCarouselSectionProps> = ({
  items,
  loading,
  busyId,
  draftById,
  readOnly = false,
  onEditInDraft,
  onToggleActive,
  onDragEnd,
}) => {
  const user = useSelector(selectUser);
  const userProfile = useSelector(selectUserProfile);
  const actor = useMemo(
    () => actorFromAuth(user, userProfile),
    [user, userProfile],
  );
  const canChangeSettings =
    !!userProfile &&
    canManageService(userProfile.role, ServiceDomain.HappeningNow);

  const {
    enabled: parallaxEnabled,
    loading: parallaxLoading,
    updating,
    load: loadParallax,
    setEnabled: setParallaxEnabled,
  } = useHappeningNowParallaxStore(
    useShallow((s) => ({
      enabled: s.enabled,
      loading: s.loading,
      updating: s.updating,
      load: s.load,
      setEnabled: s.setEnabled,
    })),
  );

  useEffect(() => {
    void loadParallax();
  }, [loadParallax]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
  );
  const sortableIds = items.map((i) => i.id);

  return (
    <section
      aria-labelledby="live-carousel-heading"
      className="rounded-[1.75rem] border-4 border-white/10 bg-white/5 p-4 backdrop-blur sm:p-5"
    >
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[#14B8A6] text-white shadow-[0_3px_0_#0F766E]">
            <Radio className="h-4 w-4" aria-hidden />
          </span>
          <div>
            <h2
              id="live-carousel-heading"
              className="text-base font-extrabold text-white sm:text-lg"
            >
              Live carousel
            </h2>
            <p className="text-[11px] font-bold text-white/70 sm:text-xs">
              What users see right now. Drag to reorder, tap pause to hide.
            </p>
          </div>
        </div>
        {!loading && items.length > 0 && (
          <span className="rounded-full bg-white/15 px-2.5 py-0.5 text-[10px] font-extrabold uppercase text-white/90 ring-2 ring-white/20">
            {items.length} {items.length === 1 ? "card" : "cards"}
          </span>
        )}
      </div>
      {canChangeSettings && (
        <div className="flex items-center mb-4 justify-between gap-2">
          <div className="flex flex-col gap-1">
            <p className="text-lg font-bold text-white/70 sm:text-lg">
              {"Parallax Mode"}
            </p>
            <p className="text-xs font-bold text-white/70 sm:text-sm">
              {
                "Enable a 3D parallax effect as users scroll the carousel. Applies the next time the app loads the carousel."
              }
            </p>
          </div>
          {parallaxLoading ? (
            <Loader2
              className="h-5 w-5 shrink-0 animate-spin text-[#14B8A6]"
              aria-label="Loading parallax setting"
            />
          ) : (
            <Switch
              size="sm"
              checked={parallaxEnabled}
              onChange={(next) => {
                if (!canChangeSettings) return;
                void setParallaxEnabled(next, actor);
              }}
              disabled={!canChangeSettings || updating}
              onColor="#14B8A6"
              offColor="rgba(255,255,255,0.25)"
              aria-label="Toggle parallax mode for the live carousel"
            />
          )}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center rounded-2xl border-4 border-white/10 bg-white/5 py-6">
          <Loader2 className="h-6 w-6 animate-spin text-[#14B8A6]" />
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border-4 border-dashed border-white/25 bg-white/5 p-4 text-center">
          <p className="text-sm font-extrabold text-white/90">
            Nothing live yet
          </p>
          <p className="mt-1 text-xs font-bold text-white/60">
            Add cards in the draft below, then click Publish carousel.
          </p>
        </div>
      ) : readOnly ? (
        <ul className="flex flex-col gap-2">
          {items.map((row) => {
            const draft = draftById.get(row.id);
            const hasDraftEdit = draft
              ? isDraftDifferentFromLive(draft, row)
              : false;
            return (
              <LiveCarouselCard
                key={row.id}
                item={row}
                busy={busyId === row.id}
                hasDraftEdit={hasDraftEdit}
                readOnly={readOnly}
                onEditInDraft={onEditInDraft}
                onToggleActive={onToggleActive}
              />
            );
          })}
        </ul>
      ) : (
        <DndContext
          id="live-carousel-dnd"
          sensors={sensors}
          collisionDetection={closestCenter}
          modifiers={[restrictToVerticalAxis]}
          onDragEnd={onDragEnd}
        >
          <SortableContext
            items={sortableIds}
            strategy={verticalListSortingStrategy}
          >
            <ul className="flex flex-col gap-2">
              {items.map((row) => {
                const draft = draftById.get(row.id);
                const hasDraftEdit = draft
                  ? isDraftDifferentFromLive(draft, row)
                  : false;
                return (
                  <LiveCarouselCard
                    key={row.id}
                    item={row}
                    busy={busyId === row.id}
                    hasDraftEdit={hasDraftEdit}
                    onEditInDraft={onEditInDraft}
                    onToggleActive={onToggleActive}
                  />
                );
              })}
            </ul>
          </SortableContext>
        </DndContext>
      )}
    </section>
  );
};
